import {
  Building2, Hospital, TreePine, Stethoscope, Users,
  User as UserIcon, UserCircle2, Baby, Heart, Accessibility,
} from "lucide-react";

/**
 * Metadata compartilhada entre o formulário de candidatura de clínica
 * (ClinicApply, ClinicProfileEdit) e a vitrine pública (PublicClinics).
 *
 * Precisa ser consistente para:
 * - Ícone + cor de cada tipo de estabelecimento (clínica, hospital, comunidade, consultório)
 * - Ícone + cor de cada público (masculino, feminino, misto, adolescente, idosos)
 * - Filtros da vitrine
 * - Preview visual no ClinicApply
 */

export const CLINIC_TYPES = [
  {
    value: "Comunidade Terapêutica",
    label: "Comunidade Terapêutica",
    short: "Comunidade",
    icon: TreePine,
    bg: "bg-emerald-50",
    text: "text-emerald-800",
    border: "border-emerald-200",
    dot: "bg-emerald-600",
  },
  {
    value: "Clínica",
    label: "Clínica",
    short: "Clínica",
    icon: Building2,
    bg: "bg-blue-50",
    text: "text-blue-800",
    border: "border-blue-200",
    dot: "bg-blue-600",
  },
  {
    value: "Hospital",
    label: "Hospital",
    short: "Hospital",
    icon: Hospital,
    bg: "bg-rose-50",
    text: "text-rose-800",
    border: "border-rose-200",
    dot: "bg-rose-600",
  },
  {
    value: "Consultório",
    label: "Consultório",
    short: "Consultório",
    icon: Stethoscope,
    bg: "bg-violet-50",
    text: "text-violet-800",
    border: "border-violet-200",
    dot: "bg-violet-600",
  },
  {
    value: "Outros",
    label: "Outros",
    short: "Outros",
    icon: Building2,
    bg: "bg-slate-50",
    text: "text-slate-700",
    border: "border-slate-200",
    dot: "bg-slate-500",
  },
];

export const AUDIENCES = [
  {
    value: "Masculino",
    label: "Masculino",
    icon: UserIcon,
    bg: "bg-sky-100",
    text: "text-sky-800",
    border: "border-sky-200",
  },
  {
    value: "Feminino",
    label: "Feminino",
    icon: UserCircle2,
    bg: "bg-pink-100",
    text: "text-pink-800",
    border: "border-pink-200",
  },
  {
    value: "Mista",
    label: "Mista",
    icon: Users,
    bg: "bg-purple-100",
    text: "text-purple-800",
    border: "border-purple-200",
  },
  {
    value: "Adolescente",
    label: "Adolescente",
    icon: Baby,
    bg: "bg-amber-100",
    text: "text-amber-800",
    border: "border-amber-200",
  },
  {
    value: "Idosos",
    label: "Idosos",
    icon: Accessibility,
    bg: "bg-teal-100",
    text: "text-teal-800",
    border: "border-teal-200",
  },
];

const _norm = (s) =>
  (typeof s === "string" ? s : String(s ?? ""))
    .toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

/** Aceita legado ("masculino"/"MASCULINO"/"Feminino") — normaliza sem acento. */
export const findAudience = (val) => {
  const n = _norm(val);
  return AUDIENCES.find((a) => _norm(a.value) === n)
    || AUDIENCES.find((a) => _norm(a.value).startsWith(n))
    || null;
};

export const findClinicType = (val) => {
  const n = _norm(val);
  return CLINIC_TYPES.find((t) => _norm(t.value) === n)
    || CLINIC_TYPES.find((t) => _norm(t.value).startsWith(n))
    || null;
};

/** Chip pequeno usado no card da vitrine e na preview. */
export const AudienceChip = ({ audience }) => {
  const meta = findAudience(audience);
  if (!meta) return (
    <span className="text-[10px] uppercase tracking-widest bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 rounded-full font-semibold">
      {audience}
    </span>
  );
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest ${meta.bg} ${meta.text} ${meta.border} border px-2 py-0.5 rounded-full font-semibold`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
};

/** Badge maior de tipo de estabelecimento — pill com ícone. */
export const EstablishmentBadge = ({ type, size = "sm" }) => {
  const meta = findClinicType(type) || CLINIC_TYPES[CLINIC_TYPES.length - 1];
  const Icon = meta.icon;
  const cls = size === "sm"
    ? "text-[10px] px-2 py-0.5"
    : "text-xs px-2.5 py-1";
  return (
    <span className={`inline-flex items-center gap-1 uppercase tracking-widest ${meta.bg} ${meta.text} ${meta.border} border ${cls} rounded-full font-bold`}>
      <Icon className="w-3.5 h-3.5" /> {meta.short}
    </span>
  );
};
